import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player';

import { STORY_EVENTS } from 'pages/Stories/constant';
import { handleNewEventLog } from 'lib/utils/events';
import ICONOGRAPHY from 'designSystem/icons/strokeIcons';

import * as Styles from './styles';

export interface Story {
  id: string;
  media_url: string;
  thumb_url: string;
}

interface StoriesViewerProps {
  stories: Story[];
  closeIos: () => void;
}

const { close: CloseIcon, 'audio-play': AudioPlayIcon } = ICONOGRAPHY;

const StoriesViewerIOS: React.FC<StoriesViewerProps> = ({
  stories,
  closeIos,
}) => {
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [progress, setProgress] = useState<number[]>(
    Array(stories.length).fill(0)
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const preloadVideoRef = useRef<HTMLVideoElement>(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [autoplayFailed, setAutoplayFailed] = useState(false);

  const [loggedSeconds, setLoggedSeconds] = useState(new Set<number>());
  const navigate = useNavigate();
  const appVersion = localStorage.getItem('native_app_version');

  const currentStory = stories[currentStoryIndex];

  const eventMeta = useMemo(
    () => ({
      route: window.location.pathname + window.location.search,
      module_name: STORY_EVENTS.UGC_HOMEPAGE_ASSET,
      placement: STORY_EVENTS.UGC_WRITE_NUDGE_BANNER,
      entity_type: STORY_EVENTS.STORY_ID,
      entity_id: currentStory.id,
      entity_position: currentStoryIndex + 1,
    }),
    [currentStory.id, currentStoryIndex]
  );

  const handleClose = useCallback(() => {
    handleNewEventLog(
      STORY_EVENTS.SCREEN_NAME,
      'Webview_Story_exit',
      eventMeta
    );
    closeIos();
  }, [eventMeta]);

  const redirectToFM = useCallback(() => {
    navigate('/home');
  }, []);

  const onStartWriting = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleNewEventLog(
        STORY_EVENTS.SCREEN_NAME,
        'Webview_Story_cta_clicked',
        eventMeta
      );
      redirectToFM();
    },
    [eventMeta]
  );

  // useEffect(() => {
  //   if (videoRef.current) {
  //     const playPromise = videoRef.current.play();

  //     if (playPromise !== undefined) {
  //       playPromise
  //         .then(() => {
  //           setAutoplayFailed(false);
  //         })
  //         .catch(() => {
  //           setAutoplayFailed(true);
  //         });
  //     }
  //   }
  // }, [currentStoryIndex]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = async () => {
      try {
        await video.play();
        // No need to setAutoplayFailed here – handled by timeupdate
      } catch (err) {
        // Still set a fallback in case video.play() doesn't even start
        setAutoplayFailed(true);
      }
    };

    tryPlay();

    const handleTimeUpdate = () => {
      if (video.currentTime > 0) {
        setAutoplayFailed(false);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [currentStoryIndex]);

  useEffect(() => {
    handleNewEventLog(
      STORY_EVENTS.SCREEN_NAME,
      'Webview_Story_viewed',
      eventMeta
    );
  }, [currentStoryIndex]);

  useEffect(() => {
    const video = videoRef.current;
    const handlePlay = () => {
      setAutoplayFailed(false);
    };

    video?.addEventListener('playing', handlePlay);
    return () => {
      video?.removeEventListener('playing', handlePlay);
    };
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const currentTime = Math.floor(video.currentTime);
    if (
      currentTime > 0 &&
      currentTime % 10 === 0 &&
      !loggedSeconds.has(currentTime)
    ) {
      handleNewEventLog(
        STORY_EVENTS.SCREEN_NAME,
        'Webview_story_played_10sec',
        eventMeta
      );
      setLoggedSeconds((prev) => new Set(prev).add(currentTime));
    }
  }, [loggedSeconds, eventMeta]);

  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;

    setProgress((prevProgress) => {
      const newProgress = [...prevProgress];

      // Mark previous story as complete
      if (currentStoryIndex > 0) {
        newProgress[currentStoryIndex - 1] = 100;
      }

      // Reset next story progress if user moves back
      if (currentStoryIndex < stories.length - 1) {
        newProgress[currentStoryIndex + 1] = 0;
      }

      return newProgress;
    });

    // Handle video loading
    const handleLoadedData = () => {
      if (!video) return;
      video.muted = !isAudioEnabled;
      video
        .play()
        .then(() => {
          setIsPaused(false);
        })
        .catch((err) => console.error('Video play error:', err));
    };

    // Update progress as video plays
    const updateProgress = () => {
      if (!video) return;

      setProgress((prevProgress) => {
        const newProgress = [...prevProgress];
        const percentage = (video.currentTime / video.duration) * 100;
        newProgress[currentStoryIndex] = percentage;
        return newProgress;
      });
    };

    const handleEnded = () => {
      setProgress((prevProgress) => {
        const newProgress = [...prevProgress];
        newProgress[currentStoryIndex] = 100;
        return newProgress;
      });

      const nextIndex = currentStoryIndex + 1;

      if (nextIndex < stories.length) {
        setCurrentStoryIndex(nextIndex);

        // Wait a bit for DOM to re-render and then try to play
        setTimeout(() => {
          const nextVideo = videoRef.current;
          if (nextVideo) {
            const playPromise = nextVideo.play();
            if (playPromise) {
              playPromise.catch((err) =>
                console.warn('Autoplay blocked on next video:', err)
              );
            }
          }
        }, 100); // Small delay to ensure next video is in DOM
      } else {
        handleClose();
      }
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('timeupdate', handleTimeUpdate);
    const interval = setInterval(updateProgress, 100);
    video.addEventListener('ended', handleEnded);

    return () => {
      clearInterval(interval);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [currentStoryIndex, stories, loggedSeconds]);

  //  Preload next video
  useEffect(() => {
    const nextIndex = currentStoryIndex + 1;
    if (nextIndex < stories.length && preloadVideoRef.current) {
      preloadVideoRef.current.src = stories[nextIndex].media_url;
      preloadVideoRef.current.load();
    }
  }, [currentStoryIndex, stories]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const touchX = e.touches[0].clientX;
      const screenWidth = window.innerWidth;

      if (touchX < screenWidth / 3) {
        setAutoplayFailed(false);
        if (currentStoryIndex > 0) {
          setCurrentStoryIndex((prev) => prev - 1);
          handleNewEventLog(
            STORY_EVENTS.SCREEN_NAME,
            'Webview_Story_back',
            eventMeta
          );
        }
      } else if (touchX > (screenWidth * 2) / 3) {
        setAutoplayFailed(false);
        if (currentStoryIndex < stories.length - 1) {
          setCurrentStoryIndex((prev) => prev + 1);
          handleNewEventLog(
            STORY_EVENTS.SCREEN_NAME,
            'Webview_Story_forward',
            eventMeta
          );
        } else {
          handleClose();
        }
      } else {
        if (!autoplayFailed && videoRef.current) {
          if (isPaused) {
            videoRef.current
              .play()
              .catch((err) => console.error('Video play error:', err));
          } else {
            videoRef.current.pause();
          }
          setIsPaused((prev) => !prev);
        }
      }
    },
    [currentStoryIndex, isPaused, eventMeta, redirectToFM, autoplayFailed]
  );

  return (
    <Styles.IOSContainer>
      {/* Show "Turn on Audio" button when video is muted */}
      {!isAudioEnabled && (
        <Styles.AudioButton
          onClick={() => {
            if (videoRef.current) {
              videoRef.current.muted = false;
              videoRef.current
                .play()
                .catch((err) => console.error('Video play error:', err));
            }
            setIsAudioEnabled(true);
          }}
        >
          <AudioPlayIcon />
        </Styles.AudioButton>
      )}

      {autoplayFailed && (
        <Styles.PlayButton
          onClick={async (e) => {
            e.stopPropagation();
            if (videoRef.current) {
              try {
                await videoRef.current.play();
                setAutoplayFailed(false);
              } catch (error) {
                console.error('Play failed again:', error);
              }
            }
          }}
        >
          <svg
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="rgba(224, 226, 235, 1)"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </Styles.PlayButton>
      )}

      {/* Video element */}
      <Styles.VideoBackground
        key={currentStory.id}
        ref={videoRef}
        src={currentStory.media_url}
        playsInline
        autoPlay
        preload="auto"
        muted={!isAudioEnabled}
        poster={currentStory.thumb_url}
        controls={false}
        webkit-playsinline="true"
        x5-playsinline="true"
        x5-video-player-type="h5"
        x5-video-player-fullscreen="false"
      />
      {/* Preload next video - hidden */}
      {/* <video ref={preloadVideoRef} style={{ display: 'none' }} preload="auto" /> */}

      {/* Touch overlay for navigation */}
      <Styles.TouchOverlay onTouchStart={handleTouchStart} />

      <Styles.Overlay>
        {/* Progress tiles */}
        <Styles.ProgressContainer>
          {stories.map((story, index) => (
            <Styles.ProgressTile key={story.id}>
              <Styles.ProgressFill style={{ width: `${progress[index]}%` }} />
            </Styles.ProgressTile>
          ))}
        </Styles.ProgressContainer>

        <Styles.CloseButton onClick={handleClose}>
          <CloseIcon size="18" stroke="rgba(224, 226, 235, 1)" />
        </Styles.CloseButton>

        <Styles.StartWritingButton onClick={onStartWriting}>
          Start writing
        </Styles.StartWritingButton>
      </Styles.Overlay>
    </Styles.IOSContainer>
  );
};

export default StoriesViewerIOS;
